import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@vinted/web-ui';
import { produce } from 'immer';
import { noop } from 'lodash';
import { AppleIDLoginStatus } from 'constants/auth';
import { initAppleId } from 'libs/utils/apple';
import { decodeJwt } from 'libs/utils/authentication';
import { loadScript } from 'libs/utils/script';
import { getAppleClientId } from 'state/next-env/selectors';
import { jsx as _jsx } from "react/jsx-runtime";
const APPLE_SDK_URL = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

const AppleButton = _ref => {
  let {
    text,
    isLoading,
    onClick = noop,
    onSuccess,
    onFailure = noop
  } = _ref;
  const {
    0: isLoadingAppleID,
    1: setIsLoadingAppleID
  } = useState(false);
  const {
    0: isInitialized,
    1: setIsInitialized
  } = useState(false);
  const clientId = useSelector(getAppleClientId);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onFailureRef = useRef(onFailure);
  onFailureRef.current = onFailure;
  const handleAppleSignInSuccess = useCallback(event => {
    const {
      detail
    } = event;
    const {
      email,
      is_private_email = false
    } = decodeJwt(detail.authorization.id_token);
    const newDetail = produce(detail, draft => {
      if (is_private_email || !email || draft.user) return;
      draft.user = {
        name: {
          firstName: '',
          lastName: ''
        },
        email
      };
    });
    onSuccessRef.current(newDetail);
    setIsLoadingAppleID(false);
  }, []);
  const handleAppleSignInFailure = useCallback(() => {
    onFailureRef.current();
    setIsLoadingAppleID(false);
  }, []);
  useEffect(() => {
    document.addEventListener(AppleIDLoginStatus.Success, handleAppleSignInSuccess);
    document.addEventListener(AppleIDLoginStatus.Failure, handleAppleSignInFailure);
    return () => {
      document.removeEventListener(AppleIDLoginStatus.Success, handleAppleSignInSuccess);
      document.removeEventListener(AppleIDLoginStatus.Failure, handleAppleSignInFailure);
    };
  }, [handleAppleSignInFailure, handleAppleSignInSuccess]);
  useEffect(() => {
    async function initialize() {
      if (!clientId) return;
      await loadScript({
        id: 'apple-sdk',
        isAsync: true,
        src: APPLE_SDK_URL
      });
      initAppleId(clientId);
      setIsInitialized(true);
    }

    initialize();
  }, [clientId]);

  function handleClick() {
    var _window$AppleID;

    onClick();
    setIsLoadingAppleID(true);
    (_window$AppleID = window.AppleID) === null || _window$AppleID === void 0 ? void 0 : _window$AppleID.auth.signIn();
  }

  if (!clientId) return null;
  return /*#__PURE__*/_jsx(Button, {
    theme: "amplified",
    iconName: "signup/apple",
    iconPosition: Button.IconPosition.Left,
    onClick: handleClick,
    disabled: !isInitialized || isLoading || isLoadingAppleID,
    isLoading: isLoading || isLoadingAppleID,
    text: text
  });
};

export default AppleButton;